import Vue from 'vue'
import Base from './Base'
import Collection from './Collection'
import clonedeep from 'lodash.clonedeep'
import clonedeepClass from '@/core/helpers/clonedeepClass.helpers'
import { AttributeNotFound } from '@/core/errors/Model.errors'
import { RetrievableMethodNotFound, MissingCsvHeaders } from '../errors/Model.errors'

export default class Model extends Base {
  constructor (attributes = {}) {
    super()

    let _attributes = {}
    let _retrievables = {}

    this.$retrievables().forEach(retrievable => {
      if (typeof this[this.constructor.$attributeGetterMethod(retrievable)] !== 'function') {
        throw new RetrievableMethodNotFound({ modelName: this.name(), retrievableName: retrievable })
      } else {
        _attributes[retrievable] = undefined
        _retrievables[retrievable] = false
      }
    })

    Vue.set(this, '_attributes', clonedeep(_attributes))
    Vue.set(this, '_relations', {})
    Vue.set(this, '_retrievables', clonedeep(_retrievables))

    // Set attributes/relations values
    for (const attribute in attributes) {
      /* istanbul ignore else */
      if (attributes.hasOwnProperty(attribute)) {
        if (this.$relations().hasOwnProperty(attribute)) {
          Vue.set(this._relations, attribute, new (this.$relations()[attribute])(attributes[attribute]))
        } else {
          this.set(attribute, attributes[attribute], true)
        }
      }
    }

    // Check primary key existance if the model
    if (this.$primaryKey() !== undefined) {
      this.throwErrorIfAttributeNotFound(this.$primaryKey())
    }
  }

  /**
   * Get primary key name
   */
  $primaryKey () {
    return 'id'
  }

  /**
   * Get attributes list
   */
  attributes () {
    return this._attributes
  }

  /**
   * Related models or collections
   *
   * @returns {{ [relation]: Class<Model|Collection> }}
   */
  $relations () {
    return {}
  }

  /**
   * Related instances
   *
   * @returns {{ [relation]: Model|Collection }}
   */
  relations () {
    return this._relations
  }

  /**
   * Retrievable attributes
   *
   * @returns {String[]}
   */
  $retrievables () {
    return []
  }

  /**
   * Retrievable attributes status
   *
   * @returns {{ [attribute]: Boolean }}
   */
  retrievables () {
    return this._retrievables
  }

  /**
   * Check if the current model has the same id
   *
   * @param {String|Number} id
   */
  is (id) {
    return this.attributes()[this.$primaryKey()] === id
  }

  /**
   * Get attribute property
   *
   * @param {String} attribute
   *
   * @return {Any}
   */
  get (attribute, placeholder) {
    try {
      const keys = attribute.split('.')
      let value = clonedeepClass(this)

      for (let i = 0; i < keys.length; i++) {
        if (this.constructor.$isAttributeExists(value, keys[i])) {
          value = clonedeep(value.attributes()[keys[i]])
        } else if (this.constructor.$isGetterExists(value, keys[i])) {
          value = value[value.constructor['$attributeGetterMethod'](keys[i])]()
        } else if (this.constructor.$isRelationExists(value, keys[i])) {
          value = clonedeepClass(value.relations()[keys[i]])
        } else if (value instanceof Collection) {
          value = clonedeep(value._models[keys[i]])
        } else if (value instanceof Object && value[keys[i]] !== undefined) {
          value = clonedeep(value[keys[i]])
        } else if (placeholder !== undefined) {
          value = clonedeep(value[keys[i]]) || placeholder
        } else {
          value = undefined
        }
      }

      if (value === undefined) {
        throw new AttributeNotFound({ attribute, model: this.name() })
      }

      return value
    } catch (error) {
      throw error
    }
  }

  /**
   * Set attribute value
   *
   * @param {String} attribute
   * @param {String} value
   *
   * @return {void}
   */
  set (attribute, value, isForce = false) {
    /* istanbul ignore else */
    if (!isForce) {
      this.throwErrorIfAttributeNotFound(attribute)
    }

    if (this.$retrievables().indexOf(attribute) !== -1) {
      Vue.set(this._retrievables, attribute, true)
    }

    Vue.set(this._attributes, attribute, value)
  }

  /**
   * Check if specific attribute exists in a model
   *
   * @param {Model} model
   * @param {String} key
   */
  static $isAttributeExists (model, key) {
    return model instanceof Model && model.attributes().hasOwnProperty(key) && (model.$retrievables().indexOf(key) === -1 || model.retrievables()[key] === true)
  }

  /**
   * Check if specific relation exists in a model
   *
   * @param {Model} model
   * @param {String} key
   */
  static $isRelationExists (model, key) {
    return model instanceof Model && model.$relations().hasOwnProperty(key)
  }

  /**
   * Check if there's a get method exists for this attribute
   *
   * @param {Model} model
   * @param {String} key
   */
  static $isGetterExists (model, key) {
    return model instanceof Model && model[model.constructor.$attributeGetterMethod(key)] instanceof Function
  }

  /**
   * Get attribute getter method
   *
   * @param {String} attributeKey
   *
   * @return {String}
   */
  static $attributeGetterMethod (attributeKey) {
    return `get${attributeKey[0].toUpperCase() + attributeKey.slice(1)}Attribute`
  }

  /**
   * Check existance of sepcific attribute
   *
   * @param {String} attribute
   *
   * @return {throwable|void}
   */
  throwErrorIfAttributeNotFound (attribute) {
    if (!this.attributes().hasOwnProperty(attribute)) {
      throw new AttributeNotFound({ attribute, model: this.name() })
    }
  }

  /**
   * Model attributes which available to csv generator
   *
   * @return {String[]}
   */
  static csvHeaders () {
    return []
  }

  /**
   * Default attributes to include in the csv
   *
   * @return {String[]}
   */
  static defaultCsvHeaders () {
    return []
  }

  /**
   * Transform csv headers
   *
   * @param {String} header
   */
  static csvHeadersTransformer (header) {
    return header
  }

  /**
   * Generate csv row for then model
   *
   * @param {Array} columns
   *
   * @return {Object}
   */
  generateCsvRow (columns) {
    return new Promise((resolve, reject) => {
      const activeColumns = columns || this.constructor.defaultCsvHeaders()

      if (!(activeColumns && activeColumns.map instanceof Function && activeColumns.length > 0)) {
        reject(new MissingCsvHeaders({ modelName: this.name() }))
      }

      Promise.all(activeColumns.map(column => this.generateCsvField(column)))
        .then(response => resolve(Object.assign({}, ...response)))
    })
  }

  /**
   * Generate csv field
   *
   * @param {String} column
   */
  generateCsvField (column) {
    return new Promise((resolve) => {
      const label = this.constructor.csvHeadersTransformer(column)
      const value = this.get(column, '')

      if (value instanceof Promise) {
        value.then(result => {
          resolve(this.constructor.$generateCsvField({ label, value: result.value }))
        })
      } else {
        resolve(this.constructor.$generateCsvField({ label, value }))
      }
    })
  }

  /**
   * Generate csv field with correct labelling
   *
   * @param {{ label, value }} options
   */
  static $generateCsvField ({ label, value }) {
    // Array
    if (value && value.constructor === Array) {
      // One value
      if (value.length === 1) {
        return { [label]: value[0] instanceof Object ? JSON.stringify(value[0]) : value[0] }
      }

      // Multiple values
      const rankLength = value.length.toString().length

      return value.reduce((acc, item, index) => {
        acc[`${label}_${(index + 1).toString().padStart(rankLength, '0')}`] = item instanceof Object ? JSON.stringify(item) : item
        return acc
      }, {})
    } else {
      return {
        [label]: value instanceof Object ? JSON.stringify(value) : value
      }
    }
  }
}
